import { createService } from "./createService"
import { SERVICE_INSTANCES } from "./serviceInstances"

const service = createService(SERVICE_INSTANCES.featureAnnouncements)

export const FEATURE_ANNOUNCEMENTS_INSTANCE = service.instanceKey

export const FEATURE_ANNOUNCEMENTS_ENDPOINTS = {
  SELECT_UNSEEN: "/feature/announcements/select_unseen",
  SELECT: "/feature/announcements/select",
  SUMMARY: "/feature/announcements/summary",
  UNSEEN_COUNT: "/feature/announcements/unseen_count",
  MARK_SEEN: "/feature/announcements/mark_seen",
} as const

/** This client. The mobile app sends "mobile". */
const PLATFORM = "web"

export type FeatureAnnouncementCategory = "feature" | "improvement" | "fix"

export interface IFeatureAnnouncement {
  id: number
  title: string
  /** Sanitized server-side before it is stored. */
  content: string
  image_url?: string | null
  /**
   * A pasted https link, not an upload: YouTube, Vimeo, Loom or a direct .mp4/.webm
   * file. Null when the announcement has no video (see features/whats-new/README.md).
   */
  video_url?: string | null
  cta_text?: string | null
  cta_link?: string | null
  /** UTC, without an offset. */
  release_date: string
  is_new: boolean
  /** Required on new announcements; older ones were filed as "feature". */
  category?: FeatureAnnouncementCategory | null
}

/** Visible announcements per category, released within the last `window_days`. */
export interface IFeatureAnnouncementSummary
  extends Record<FeatureAnnouncementCategory, number> {
  window_days: number
}

export const featureAnnouncementsService = {
  instanceKey: service.instanceKey,

  /**
   * At most 5 unseen announcements from the last 30 days that are marked for the popup:
   * drives the login popup.
   */
  selectUnseen: () =>
    service.get<IBaseRes<IFeatureAnnouncement[]>>(
      FEATURE_ANNOUNCEMENTS_ENDPOINTS.SELECT_UNSEEN,
      { platform: PLATFORM }
    ),

  /** The What's New screen: everything visible, newest first. `category` narrows it on the server. */
  select: (params: {
    page: number
    limit: number
    category?: FeatureAnnouncementCategory
  }) =>
    service.get<IBaseRes<IPaginatedPayload<IFeatureAnnouncement>>>(
      FEATURE_ANNOUNCEMENTS_ENDPOINTS.SELECT,
      { platform: PLATFORM, ...params }
    ),

  /**
   * How many announcements from the last 30 days the user has not seen, popup or not:
   * drives the nav dot.
   */
  unseenCount: () =>
    service.get<IBaseRes<{ count: number }>>(
      FEATURE_ANNOUNCEMENTS_ENDPOINTS.UNSEEN_COUNT,
      { platform: PLATFORM }
    ),

  /** Per-category counts for the "Last 30 days" card. */
  summary: () =>
    service.get<IBaseRes<IFeatureAnnouncementSummary>>(
      FEATURE_ANNOUNCEMENTS_ENDPOINTS.SUMMARY,
      { platform: PLATFORM }
    ),

  /** Marks one announcement seen: called when the user opens it or plays its video. */
  markSeen: (id: number) =>
    service.post(
      `${FEATURE_ANNOUNCEMENTS_ENDPOINTS.MARK_SEEN}?platform=${PLATFORM}`,
      { id }
    ),
}
